import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ShieldCheck, ShieldAlert, Clock, Upload } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { driverAPI } from '../services/api';

const STATUS_META = {
  unverified: { label: 'Not verified', color: '#94a3b8', icon: ShieldAlert },
  pending: { label: 'Under review', color: '#f59e0b', icon: Clock },
  approved: { label: 'Verified', color: '#10b981', icon: ShieldCheck },
  rejected: { label: 'Rejected', color: '#ef4444', icon: ShieldAlert }
};

const DriverVerificationCard = ({ onStatusChange }) => {
  const [status, setStatus] = useState(null);
  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseFrontFile, setLicenseFrontFile] = useState(null);
  const [licenseBackFile, setLicenseBackFile] = useState(null);
  const [insuranceFile, setInsuranceFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchStatus = async () => {
    try {
      const data = await driverAPI.getVerificationStatus();
      setStatus(data);
      onStatusChange && onStatusChange(data.verification_status);
    } catch (error) {
      console.error('Error fetching verification status:', error);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!licenseNumber || !licenseFrontFile || !licenseBackFile || !insuranceFile) {
      toast({
        title: 'Missing information',
        description: 'Please provide your license number, both sides of your licence, and proof of insurance',
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);
    try {
      await driverAPI.submitVerification(licenseNumber, licenseFrontFile, licenseBackFile, insuranceFile);
      toast({
        title: 'Documents submitted',
        description: 'An admin will review them shortly'
      });
      await fetchStatus();
    } catch (error) {
      toast({
        title: 'Submission failed',
        description: error.response?.data?.detail || 'Could not submit documents',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!status) return null;

  const meta = STATUS_META[status.verification_status] || STATUS_META.unverified;
  const Icon = meta.icon;
  const showForm = status.verification_status === 'unverified' || status.verification_status === 'rejected';

  return (
    <Card className="border-0 mb-6" style={{ background: 'rgba(23, 40, 58, 0.9)', border: `1px solid ${meta.color}40` }}>
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Icon className="w-5 h-5" style={{ color: meta.color }} />
          Driver Verification: <span style={{ color: meta.color }}>{meta.label}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {status.verification_status === 'approved' && (
          <p className="text-sm text-slate-400">You're verified and can go online to receive jobs.</p>
        )}
        {status.verification_status === 'pending' && (
          <p className="text-sm text-slate-400">
            Your license and insurance documents are being reviewed. You'll be able to go online once approved.
          </p>
        )}
        {status.verification_status === 'rejected' && (
          <p className="text-sm text-red-400 mb-3">
            Your submission was rejected{status.rejection_reason ? `: ${status.rejection_reason}` : ''}. Please resubmit.
          </p>
        )}

        {showForm && (
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div>
              <Label className="text-slate-300">Driving license number</Label>
              <Input
                value={licenseNumber}
                onChange={(e) => setLicenseNumber(e.target.value)}
                placeholder="e.g. MORGA657054SM9IJ"
                className="mt-1 bg-wheely-navy-900 border-wheely-navy-700 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300">Driving license — front (photo or PDF)</Label>
              <Input
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                onChange={(e) => setLicenseFrontFile(e.target.files?.[0] || null)}
                className="mt-1 bg-wheely-navy-900 border-wheely-navy-700 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300">Driving license — back (photo or PDF)</Label>
              <Input
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                onChange={(e) => setLicenseBackFile(e.target.files?.[0] || null)}
                className="mt-1 bg-wheely-navy-900 border-wheely-navy-700 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300">Proof of insurance (photo or PDF)</Label>
              <Input
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                onChange={(e) => setInsuranceFile(e.target.files?.[0] || null)}
                className="mt-1 bg-wheely-navy-900 border-wheely-navy-700 text-white"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              style={{ background: 'linear-gradient(to right, #E17431, #EB9868)' }}
              className="text-white"
            >
              <Upload className="w-4 h-4 mr-2" />
              {submitting ? 'Submitting...' : 'Submit for review'}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
};

export default DriverVerificationCard;
